import { useAuthStore } from "@/stores/authStore";
import {
  useGetComplianceEvaluation,
  useGetGroupLifeInsurance,
} from "@/hooks/compliance/compliance.hook";
import ComplianceService from "@/services/compliance/compliance.service";
import {
  aggregateYearForCertificate,
  normalizeCurrentYearReportResponse,
  parseSumAssured,
} from "@/utils/pccCertificateAggregates";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";

interface PCCCertificateProps {
  certificateNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  handleDownload?: () => void;
}

function formatIssueDate(iso?: string): string {
  const d = iso ? new Date(iso) : new Date();
  return d
    .toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })
    .toUpperCase();
}

function formatExpiryDate(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const day = d.getDate();
  const month = d.toLocaleDateString("en-GB", { month: "short" });
  const year = d.getFullYear();
  return `${day}, ${month} ${year}`;
}

const PCCCertificate = ({
  certificateNumber,
  issueDate,
  handleDownload,
  expiryDate,
}: PCCCertificateProps) => {
  const { currentEmployer } = useAuthStore();
  const { data: complianceEvaluation } = useGetComplianceEvaluation();
  const { data: groupLifeInsurance } = useGetGroupLifeInsurance();

  const certNumber =
    certificateNumber || `PCC${complianceEvaluation?.id || "000490352"}`;
  const issueDateFormatted = formatIssueDate(issueDate);
  const expiryDateFormatted = formatExpiryDate(expiryDate);

  const formattedCompanyName =
    currentEmployer?.companyName?.toUpperCase() || "COMPANY NAME";
  const employerCode = currentEmployer?.employerCode || "—";
  const rcNumber =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (currentEmployer as any)?.rcNumber ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (currentEmployer as any)?.cacNumber ||
    "—";

  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () => [currentYear - 3, currentYear - 2, currentYear - 1],
    [currentYear]
  );

  const yearReportQueries = useQueries({
    queries: years.map((year) => ({
      queryKey: ["current-year-reports", { year }],
      queryFn: () => ComplianceService.getCurrentYearReports({ year }),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const perYearAggregates = yearReportQueries.map((q) =>
    aggregateYearForCertificate(normalizeCurrentYearReportResponse(q.data))
  );

  const gliSumAssured = parseSumAssured(
    groupLifeInsurance?.data?.sum_assured
  );

  const verificationUrl = certNumber
    ? `${window.location.origin}/certificate/${certNumber}`
    : "";

  return (
    <div
      onClick={handleDownload}
      className="w-[210mm] min-h-[297mm] cursor-pointer bg-white px-12 pt-8 pb-10 font-clash-display relative shadow-lg overflow-hidden"
      id="pcc-certificate"
    >
      {/* UnifrakturMaguntia for "This is to certify that" line */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=UnifrakturMaguntia&display=swap');`}</style>

      {/* PENCOM watermark */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none z-0"
        aria-hidden="true"
      >
        <span
          style={{
            fontSize: "140px",
            fontWeight: "bold",
            color: "#000",
            opacity: 0.045,
            transform: "rotate(30deg)",
            whiteSpace: "nowrap",
            userSelect: "none",
          }}
        >
          PENCOM
        </span>
      </div>

      {/* Main content above watermark */}
      <div className="relative z-10 flex flex-col h-full">

        {/* 1. Logo centered + PCC number top-right */}
        <div className="relative flex justify-center mb-4">
          <img
            src="/assets/icons/pencom_logo.jpg"
            alt="PenCom Logo"
            className="w-20 h-auto"
          />
          <div className="absolute right-0 top-0 text-right">
            <p className="text-sm font-semibold text-black tracking-wide">
              {certNumber}
            </p>
            <p className="text-xs font-semibold text-red-600">Original</p>
          </div>
        </div>

        {/* 2. National Pension Commission */}
        <p className="text-center font-semibold text-[15px] text-black mb-3">
          National Pension Commission
        </p>

        {/* 3. HR */}
        <hr className="border-black mb-3" />

        {/* 4. Pension Clearance Certificate */}
        <p className="text-center font-bold text-lg text-black mb-4">
          Pension Clearance Certificate
        </p>

        {/* 5. Employer Code - left aligned */}
        <p className="text-[13px] text-black mb-5">
          Employer Code:{" "}
          <span className="underline underline-offset-2 font-medium">
            {employerCode}
          </span>
        </p>

        {/* 6. This is to certify that */}
        <p
          className="text-center text-[38px] text-black mb-5 leading-tight"
          style={{ fontFamily: "'UnifrakturMaguntia', serif" }}
        >
          This is to certify that
        </p>

        {/* 7. Company name + RC No on same line */}
        <div className="flex justify-center items-baseline gap-x-10 mb-5">
          <span
            className="font-bold text-[15px] text-black"
            style={{ borderBottom: "1.5px solid #000", paddingBottom: "2px" }}
          >
            {formattedCompanyName}
          </span>
          <span className="text-[13px] text-black whitespace-nowrap">
            RC No.{" "}
            <span
              style={{ borderBottom: "1.5px solid #000", paddingBottom: "2px" }}
            >
              {rcNumber}
            </span>
          </span>
        </div>

        {/* 8. Compliance statements */}
        <p className="text-center text-[13px] text-black mb-1">
          has complied with the provisions of the Pension Reform Act 2014
        </p>
        <p className="text-center text-[13px] text-black mb-4">
          The details of the compliance are as follows
        </p>

        {/* 9. Compliance table */}
        <table
          className="w-full text-black mb-5 text-xs"
          style={{ border: "1px solid #000", borderCollapse: "collapse" }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid #000", background: "#f5f5f5" }}>
              <th
                className="p-2 text-left font-bold"
                style={{ borderRight: "1px solid #000" }}
              >
                Description
              </th>
              {years.map((year, idx) => (
                <th
                  key={year}
                  className="p-2 text-center font-bold"
                  style={{
                    borderRight: idx < years.length - 1 ? "1px solid #000" : "none",
                  }}
                >
                  Year {year}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Number of Employees */}
            <tr style={{ borderBottom: "1px solid #000" }}>
              <td className="p-2" style={{ borderRight: "1px solid #000" }}>
                Number of Employees
              </td>
              {years.map((year, idx) => {
                const q = yearReportQueries[idx];
                const agg = perYearAggregates[idx];
                const count = q.isPending || q.isError ? null : agg.employeeCount;
                return (
                  <td
                    key={year}
                    className="p-2 text-center"
                    style={{
                      borderRight: idx < years.length - 1 ? "1px solid #000" : "none",
                    }}
                  >
                    {count == null ? "–" : count.toLocaleString("en-NG")}
                  </td>
                );
              })}
            </tr>

            {/* Pension Contributions */}
            <tr style={{ borderBottom: "1px solid #000" }}>
              <td className="p-2" style={{ borderRight: "1px solid #000" }}>
                Pension Contributions Remitted to Employees RSAs (NGN)
              </td>
              {years.map((year, idx) => {
                const q = yearReportQueries[idx];
                const agg = perYearAggregates[idx];
                const show = !q.isPending && !q.isError;
                return (
                  <td
                    key={year}
                    className="p-2 text-center"
                    style={{
                      borderRight: idx < years.length - 1 ? "1px solid #000" : "none",
                    }}
                  >
                    {show
                      ? agg.pensionTotal.toLocaleString("en-NG", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "–"}
                  </td>
                );
              })}
            </tr>

            {/* Sum Assured */}
            <tr>
              <td className="p-2" style={{ borderRight: "1px solid #000" }}>
                Sum Assured for Group Life Insurance
              </td>
              {years.map((year, idx) => {
                const isLatest = idx === years.length - 1;
                const showGli =
                  isLatest && gliSumAssured != null && gliSumAssured > 0;
                return (
                  <td
                    key={year}
                    className="p-2 text-center"
                    style={{
                      borderRight: idx < years.length - 1 ? "1px solid #000" : "none",
                    }}
                  >
                    {showGli
                      ? gliSumAssured.toLocaleString("en-NG", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "–"}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>

        {/* 10. Expiry date - right aligned */}
        <p className="text-right text-[13px] text-black mb-8">
          This certificate expires{" "}
          <span className="font-bold">{expiryDateFormatted}</span>
        </p>

        {/* 11. Footer: QR code left, signature right */}
        <div className="flex justify-between items-end mt-auto">
          {/* QR code + date issued */}
          <div className="flex flex-col items-start gap-y-2">
            {verificationUrl ? (
              <QRCodeSVG value={verificationUrl} size={80} />
            ) : (
              <div className="w-20 h-20 border border-black flex items-center justify-center text-xs text-gray-400">
                QR
              </div>
            )}
            <p className="text-xs text-black">Date issued: {issueDateFormatted}</p>
          </div>

          {/* Signature — no image on file, using ruled line */}
          <div className="flex flex-col items-center">
            <div
              className="w-48 mb-1"
              style={{ borderBottom: "1.5px solid #000" }}
            >
              <div className="h-10" />
            </div>
            <p className="text-[13px] font-semibold text-black">Ahmed Lawan</p>
            <p className="text-[13px] text-black">Approved signatory</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PCCCertificate;
